import * as React from "react";
import GlobalStyles from "@mui/joy/GlobalStyles";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import Divider from "@mui/joy/Divider";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton, { listItemButtonClasses } from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import SupportRoundedIcon from "@mui/icons-material/SupportRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ArrowBackIosRounded from "@mui/icons-material/ArrowBackIosRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { closeSidebar } from "../utils";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Link, useNavigate } from "react-router-dom";
import { RootMenu } from "../config/RootMenu";
import { ClientMenu } from "../config/ClientMenu";
import { RouteComponentProps } from "../models/RouteComponent";
import { UserTypes } from "../enum/UserTypes";
import { ConsultantMenu } from "../config/ConsultantMenu";
import { setUserDetails, clearStoredActiveSession } from "../service/session-service";
import { UserSession } from "../models/UserSession";
import { SideMenu, childSideMenu } from "../models/SideMenu";
import { ClientSiteMenu } from "../config/ClientSiteMenu";
import { useSiteContext } from "../context/siteContext";
import { humanToKey } from "../utils/generalUtils";
import { Button } from "primereact/button";

function Toggler({
  defaultExpanded = false,
  renderToggle,
  children,
}: {
  defaultExpanded?: boolean;
  children: React.ReactNode;
  renderToggle: (params: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultExpanded);
  return (
    <React.Fragment>
      {renderToggle({ open, setOpen })}
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          transition: "0.2s ease",
          "& > *": {
            overflow: "hidden",
          },
        }}
      >
        {children}
      </Box>
    </React.Fragment>
  );
}

export default function SideMenuComponent(props: RouteComponentProps) {
  const { signOut } = useAuthenticator();
  const { site, setSite } = useSiteContext();
  const [activeMenu, setActiveMenu] = React.useState(RootMenu);
  const [activeType, setActiveType] = React.useState(UserTypes.DSF);
  const navigate = useNavigate();

  const session = props.session || {};

  const loadMenu = () => {
    setUserDetails(session);
    switch (session.active_type) {
      case UserTypes.DSF:
        setActiveMenu(RootMenu);
        setActiveType(UserTypes.DSF);
        setSite(null);
        break;
      case UserTypes.Admin:
      case UserTypes.Employee:
      case UserTypes.Client:
        setActiveMenu(ClientMenu);
        setActiveType(UserTypes.Client);
        setSite(null);
        break;
      case UserTypes.Consultant:
        setActiveMenu(ConsultantMenu);
        setActiveType(UserTypes.Consultant);
        setSite(null);
        break;
      case UserTypes.Site:
        setActiveMenu(ClientSiteMenu);
        setActiveType(UserTypes.Site);
        break;
      default:
        setActiveMenu(RootMenu);
        setSite(null);
        break;
    }
  };

  const logOut = () => {
    props.setSession({} as any);
    // Clear stored client/site from localStorage on sign out
    clearStoredActiveSession();
    navigate("/");
    signOut();
    window.location.reload();
  };

  const navigatePage = (page: SideMenu | childSideMenu) => {
    let breadcrumbs = props.breadcrumbs;
    const length = (breadcrumbs?.length || 1) - 1;
    breadcrumbs[length] = {
      label: page.label,
      link: page.link,
      userType: activeType,
    };
    props.setBreadcrumbs(breadcrumbs);
    navigate(page.link);
  };

  React.useEffect(() => {
    loadMenu();
  }, [session, site]);

  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: "fixed", md: "sticky" },
        transform: {
          xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
          md: "none",
        },
        transition: "transform 0.4s, width 0.4s",
        zIndex: 10000,
        height: "100dvh",
        width: "var(--Sidebar-width)",
        top: 0,
        p: 2,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      <GlobalStyles
        styles={theme => ({
          ":root": {
            "--Sidebar-width": "220px",
            [theme.breakpoints.up("lg")]: {
              "--Sidebar-width": "250px",
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: "fixed",
          zIndex: 9998,
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          opacity: "var(--SideNavigation-slideIn)",
          backgroundColor: "var(--joy-palette-background-backdrop)",
          transition: "opacity 0.4s",
          transform: {
            xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))",
            lg: "translateX(-100%)",
          },
        }}
        onClick={() => closeSidebar()}
      />

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {/* <IconButton variant="soft" color="primary" size="sm">
          <BrightnessAutoRoundedIcon />
        </IconButton> */}
        <img src={require("../assets/icon/DSF_Logo-Low-trimmed2.png")} style={{ width: "100%" }} />
        {/* <Typography level="title-lg">DSF</Typography>
        <ColorSchemeToggle sx={{ ml: 'auto' }} /> */}
      </Box>
      {/* <Input size="sm" startDecorator={<SearchRoundedIcon />} placeholder="Search" /> */}

      {session.site_name && session.site_id && (
        <>
          <Divider />
          <label style={{ textAlign: "center" }}>{session.site_name}</label>
          <Divider />
        </>
      )}

      {session.client_name && !session.site_id && (
        <>
          <Divider />
          <label style={{ textAlign: "center" }}>{session.client_name}</label>
          <Divider />
        </>
      )}
      <Box
        sx={{
          minHeight: 0,
          overflow: "hidden auto",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List
          size="sm"
          sx={{
            gap: 1,
            "--List-nestedInsetStart": "30px",
            "--ListItem-radius": theme => theme.vars.radius.sm,
          }}
        >
          {activeMenu.map((menu, i) =>
            menu.childMenu ? (
              <ListItem nested key={`menu-${i}`} style={{ display: "flex", width: "100%" }}>
                <Toggler
                  renderToggle={({ open, setOpen }) => (
                    <ListItemButton onClick={() => setOpen(!open)}>
                      <menu.icon />
                      <ListItemContent>
                        <Typography level="title-sm">{menu.label}</Typography>
                      </ListItemContent>
                      <KeyboardArrowDownIcon sx={{ transform: open ? "rotate(180deg)" : "none" }} />
                    </ListItemButton>
                  )}
                >
                  <List sx={{ gap: 0.5 }}>
                    {menu.childMenu.map((childMenu, y) => (
                      <ListItem key={`childMenu-${y}`}>
                        <ListItemButton
                          onClick={event => {
                            navigatePage(childMenu);
                          }}
                        >
                          {childMenu.label}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Toggler>
              </ListItem>
            ) : (
              <ListItem
                key={`menu-${i}`}
                id={`${menu.id ? menu.id : humanToKey(menu.label)}`}
                style={{ display: "flex", width: "100%" }}
              >
                <ListItemButton
                  role="menuitem"
                  onClick={event => {
                    navigatePage(menu);
                  }}
                >
                  <menu.icon />
                  <ListItemContent>
                    <Typography
                      level="title-sm"
                      // color={menu.id === "incomplete-setup-menu" ? "danger" : "primary"}
                      style={{ color: menu.id === "incomplete-setup-menu" ? "red" : undefined }}
                    >
                      {menu.label}
                    </Typography>
                  </ListItemContent>
                  {menu.badge ? (
                    <Chip size="sm" color="primary" variant="solid">
                      {menu.badge}
                    </Chip>
                  ) : null}
                </ListItemButton>
              </ListItem>
            )
          )}
        </List>

        <List
          size="sm"
          sx={{
            mt: "auto",
            flexGrow: 0,
            "--ListItem-radius": theme => theme.vars.radius.sm,
            "--List-gap": "8px",
            mb: 2,
          }}
        >
          {/* <ListItem>
            <ListItemButton>
              <SupportRoundedIcon />
              Support
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton>
              <SettingsRoundedIcon />
              Settings
            </ListItemButton>
          </ListItem> */}
        </List>
        {/* <Card
          invertedColors
          variant="soft"
          color="warning"
          size="sm"
          sx={{ boxShadow: 'none' }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography level="title-sm">Used space</Typography>
            <IconButton size="sm">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <Typography level="body-xs">
            Your team has used 80% of your available space. Need more?
          </Typography>
          <LinearProgress variant="outlined" value={80} determinate sx={{ my: 1 }} />
          <Button size="sm" variant="solid">
            Upgrade plan
          </Button>
        </Card> */}
      </Box>

      {/* Back to Main Portal */}
      {site?.id && (
        <>
          <Divider />
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", px: 1 }}>
            <Button
              label="Main Portal"
              icon="pi pi-angle-left"
              outlined
              size="small"
              style={{
                width: '100%',
                borderColor: '#1976d2',
                color: '#1976d2'
              }}
              onClick={() => {
                const newSession: any = { active_type: UserTypes.Client, site_id: null, site_name: null };
                props.setSession((prev) => ({ ...prev, ...newSession }));
                props.setBreadcrumbs((prev) => {
                  const newBreadcrumbs = [...prev];
                  console.log("Current breadcrumbs before reset:", newBreadcrumbs);
                  const index = newBreadcrumbs.findIndex(b => b.link === '/site-table');
                  if (index !== -1) {
                    newBreadcrumbs.splice(index);
                  }
                  return newBreadcrumbs;
                });
                navigate("/client-dashboard");
              }}
            />
          </Box>
        </>
      )}

      <Divider />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Avatar variant="outlined" size="sm" src={require("../assets/icon/DSF-D.png")} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography level="title-sm">
            {props?.session?.name} {props?.session?.surname}
          </Typography>
          <Typography level="body-xs">{props?.session?.email}</Typography>
        </Box>
        <IconButton size="sm" variant="plain" color="neutral">
          <LogoutRoundedIcon onClick={() => logOut()} />
        </IconButton>
      </Box>
    </Sheet>
  );
}
